import { timeAgoString } from "@/utils";
import { getPARoute } from "@/utils/paRoutes";
import { moduleStepRoute } from "@/models/intakeSteps";
import { OPEN_STATES, STATE_LABELS, STATE_TONES } from "@/models/worklistRow";
import { EXPIRY_WARNING_DAYS, PA_STATUS_MAP } from "@/models/paStatus";
import { intakeStatusLabel } from "@/models/intakeStatus";
import { caseModule, currentModuleKey, MODULE_ORDER, withMedicalNecessity } from "@/models/caseModules";
const PIPELINE = ["fax_intake", "benefits_verification", "clinical_review", "prior_auth"];
const INTAKE_MODULES = new Set(["fax_intake", "clinical_review", "necessity_check"]);
const ORDER_MODULES = new Set(["benefits_verification", "clinical_review", "necessity_check", "prior_auth"]);
export const GHOST_TEXT = {
    not_started: "Not started",
    skipped: "Skipped",
    direct: "PA started directly",
    not_reached: "Not reached",
};
/** The company's workflow steps in order; every journey row keeps a column for each. */
export function pipelineFor(visible) {
    return PIPELINE.filter((key) => visible?.includes(key));
}
export function sharedColumns(lanes, pipeline = []) {
    const used = new Set([...pipeline, ...lanes.flatMap((lane) => lane.nodes.map((node) => node.moduleKey))]);
    const known = MODULE_ORDER.filter((key) => used.has(key));
    return [...known, ...[...used].filter((key) => !MODULE_ORDER.includes(key))];
}
/** Why a workflow step has no node on this lane; null when it has one, is not a workflow column, or the lane is only a document. */
export function ghostOf(key, lane, pipeline) {
    if (!lane.onOrderPath || !pipeline.includes(key) || lane.nodes.some((node) => node.moduleKey === key))
        return null;
    const at = PIPELINE.indexOf(key);
    if (lane.nodes.some((node) => PIPELINE.indexOf(node.moduleKey) > at))
        return lane.fromIntake ? "skipped" : "direct";
    return lane.open ? "not_started" : "not_reached";
}
export function holdersText(names) {
    if (!names?.length)
        return "";
    return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
}
export function waitingText(at) {
    if (!at)
        return "";
    const span = timeAgoString(new Date(at));
    return span.startsWith("0 ") ? "Just arrived" : `Waiting ${span}`;
}
export function personName(first, last) {
    const parts = [last?.trim(), first?.trim()].filter(Boolean);
    return parts.length === 2 ? `${parts[0]}, ${parts[1]}` : parts[0] || "Unnamed patient";
}
export function ageOn(dob, today = new Date()) {
    if (!dob)
        return null;
    const [year, month, day] = dob.split("-").map(Number);
    if (!year)
        return null;
    let age = today.getFullYear() - year;
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day))
        age -= 1;
    return age;
}
export function shortDate(value) {
    if (!value)
        return "";
    const [year, month, day] = value.slice(0, 10).split("-");
    return year && month && day ? `${month}/${day}/${year}` : "";
}
export function daysUntil(value, today = new Date()) {
    if (!value)
        return null;
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);
    const target = Date.UTC(year, month - 1, day);
    const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((target - now) / 86_400_000);
}
export function expiryTone(days) {
    if (days === null)
        return null;
    if (days < 0)
        return "expired";
    return days <= EXPIRY_WARNING_DAYS ? "soon" : "ok";
}
export function daysLeftText(days) {
    if (days === null)
        return "";
    if (days < 0)
        return "expired";
    return days === 0 ? "today" : `${days}d`;
}
export function subjectTitle(subject) {
    if (subject.drug)
        return subject.drug;
    if (subject.kind === "prior_auth")
        return "Prior authorization";
    return subject.documentTypes[0] || "Referral";
}
export function subjectSubtitle(subject) {
    if (subject.kind === "prior_auth" || subject.drug)
        return subject.payer || "";
    return subject.payer || subject.documentTypes.slice(1).join(", ");
}
function closedRoute(step) {
    return !OPEN_STATES.has(step.state);
}
export function stepRouteFor(step, subject, paStatus) {
    if (step.hidden)
        return null;
    const links = subject.links;
    const intakeOrderId = step.intakeOrderId ?? links.intakeOrderId;
    const route = moduleStepRoute(step.moduleKey, { documentKey: step.intakeDocumentKey ?? null, intakeOrderId }, closedRoute(step));
    if (route || currentModuleKey(step.moduleKey) === "fax_intake")
        return route;
    if (links.orderKey)
        return getPARoute(links.orderKey, paStatus ?? undefined);
    return null;
}
function nodeText(step, paStatus) {
    const status = step.moduleKey === "prior_auth" && OPEN_STATES.has(step.state) ? paStatus || step.label : null;
    if (status)
        return PA_STATUS_MAP[status]?.label ?? status;
    return step.outcome || step.label || STATE_LABELS[step.state];
}
export function laneOf(source, paStatus, medicalNecessity = true) {
    const caseId = "caseId" in source ? source.caseId : 0;
    const nodes = withMedicalNecessity(source.steps, medicalNecessity).map((step) => ({
        id: step.id,
        moduleKey: currentModuleKey(step.moduleKey),
        label: caseModule(step.moduleKey).label,
        short: caseModule(step.moduleKey).short,
        icon: caseModule(step.moduleKey).icon,
        state: step.state,
        tone: step.hidden ? "muted" : STATE_TONES[step.state],
        isOpen: OPEN_STATES.has(step.state),
        hidden: step.hidden,
        text: step.hidden ? "Not enabled for your organization" : nodeText(step, paStatus),
        at: step.completedAt || step.stateChangedAt || step.startedAt,
        to: stepRouteFor(step, source, paStatus),
    }));
    const current = nodes.find((node) => node.isOpen && !node.hidden) ?? null;
    const last = [...nodes].reverse().find((node) => !node.hidden) ?? null;
    const links = source.links;
    return {
        caseId,
        title: subjectTitle(source),
        subtitle: subjectSubtitle(source),
        nodes,
        current,
        appeal: source.appeal,
        to: (current ?? last)?.to ?? null,
        open: "isOpen" in source ? source.isOpen : true,
        fromIntake: Boolean(links?.intakeDocumentId || links?.documentKey || links?.intakeOrderId) ||
            nodes.some((node) => INTAKE_MODULES.has(node.moduleKey)),
        onOrderPath: Boolean(links?.intakeOrderId || links?.orderKey) || nodes.some((node) => ORDER_MODULES.has(node.moduleKey)),
    };
}
export function caseStatusText(item) {
    if (item.kind === "prior_auth" && item.paStatus) {
        return PA_STATUS_MAP[item.paStatus]?.label ?? item.paStatus;
    }
    return intakeStatusLabel(item.orderStatus || item.docStatus);
}
export class PatientGridRow {
    id;
    name;
    dob;
    mrn;
    openWork;
    needsAttention;
    nextExpiry;
    daysLeft;
    lastActivityAt;
    lanes;
    moreLanes;
    navigateTo;
    raw;
    constructor(raw, medicalNecessity = true) {
        this.raw = raw;
        this.id = raw.id;
        this.name = personName(raw.firstName, raw.lastName);
        this.dob = raw.dob;
        this.mrn = raw.mrn ?? "";
        this.openWork = raw.openWork;
        this.needsAttention = raw.needsAttention;
        this.nextExpiry = raw.nextExpiry;
        this.daysLeft = daysUntil(raw.nextExpiry);
        this.lastActivityAt = raw.lastActivityAt;
        this.lanes = raw.journeys.map((journey) => laneOf(journey, null, medicalNecessity));
        this.moreLanes = raw.moreJourneys;
        this.navigateTo = { name: "mission-control-patient", params: { id: raw.id } };
    }
    /** The rows of one grid page. Medical Necessity lanes show unless the page's modules leave clinical_review out. */
    static fromPage(page) {
        const medicalNecessity = page.visibleModules?.includes("clinical_review") ?? true;
        return page.results.map((raw) => new PatientGridRow(raw, medicalNecessity));
    }
}
